/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2020, 2021
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'react',
	'mobx-react',
	'@ba/ba-ui-carbon',
	'prop-types'
], function (
	React,
	MobxReact,
	BA_UI_CARBON,
	PropTypes
) {

	const getUISpec = (props) => {
		const { item } = props;
		let spec = item;
		if (item && item.editor && item.editor.uiControl) {
			spec = item.editor.uiControl;
		}
		return spec;
	};

	const STEP_MULTIPLIER = 4;
	const DEFAULT_STEP = 1;
	const DEFAULT_MAX_VALUE = 1;
	const DEFAULT_MIN_VALUE = 0;
	/**
	 * @class Slider
	 * @description Slider property control class. Supports 2 types of input: regular text and numeric input
	 * 
	 * @example Example props usage to render a number input:
	 * {
	 * 		item: {
	 *			editor: {
	 *				uiControl: {
	 *					id: 'prop1', // required field
	 *					label: 'property label', // required field
	 *					readOnly: false, // default to false
	 * 					range: { min: 0, max: 100} // default to [0,1],
	 * 					step: 1, // default to 1,
	 * 					defaultValue: 1, // default to 1
	 * 					sliderType: 'percentage' // if 'percentage', the value will be converted to percentage
	 *				}
	 *			},
	 *			setPropertyValue: (value) => { console.log(`property value set to ${value}`); }, // required field
	 * 			getPropertyValue: () => value
	 *			value: 23  // required field
	 *		},
	 *		style: 'width: "140px"',
	 *		className: 'myControlClass'
	 * }
	 * 
	 */
	class Slider extends React.Component {

		render() {
			const { style, className, item } = this.props;
			const containerProps = {
				style,
				className,
				'data-property-id': item.id
			};
			return (
				<div {...containerProps}>
					{this.renderControl()}
				</div>
			);
		}

		renderControl() {
			const { item } = this.props;

			let { setPropertyValue, value } = item;
			let resultValue;
			let {
				id,
				sliderType,
				readOnly,
				label,
				step,
				range,
				defaultValue
			} = getUISpec(this.props);

			let max = range.max || DEFAULT_MAX_VALUE;
			let min = range.min || DEFAULT_MIN_VALUE;
			step = step || DEFAULT_STEP;
			if(value === undefined) {
				resultValue = typeof defaultValue !== 'undefined' ? defaultValue : DEFAULT_MAX_VALUE;
			} else {
				resultValue = value;
			}
			if (sliderType === 'percentage') {
				resultValue = parseInt(resultValue * 100, 10);
			}
			return (
				<BA_UI_CARBON.Slider
					id={id}
					labelText={label}
					step={step}
					value={resultValue}
					stepMultiplier={step * STEP_MULTIPLIER}
					max={max}
					min={min}
					disabled={readOnly}
					onChange={(event) => {
						let processedValue = event.value;
						if (sliderType === 'percentage') {
							processedValue = processedValue * 0.01;
						}
						setPropertyValue(processedValue);
					}}
					light
				/>
			);
		}
	}

	Slider.propTypes = {
		item: PropTypes.shape({
			editor: PropTypes.shape({
				uiControl: PropTypes.shape({
					id: PropTypes.string.isRequired,
					label: PropTypes.string,
					readOnly: PropTypes.bool,
					range: PropTypes.object,
					step: PropTypes.number,
					defaultValue: PropTypes.number,
					sliderType: PropTypes.string
				})
			}),
			id: PropTypes.string.isRequired,
			setPropertyValue: PropTypes.func.isRequired,
			value: PropTypes.any
		}),
		style: PropTypes.object,
		className: PropTypes.string
	};
	Slider.defaultProps = {
		style: {}
	};

	return MobxReact.observer(Slider);
});